// F-081 — Export comptable annuel : contrôle des totaux, registre CSV, fichier des écritures, archive des pièces.
import { useState } from "react";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Card, Empty, ErrorBox, KeyValues, Loading, Notice, PageHeader, Select, Stat, StatusChip, Table } from "@core/ui";
import { Amount, YearSelect, currentYear, useRefs } from "./shared";

type Check = {
  year: number;
  period: { from: string; to: string };
  scope: { owner_id: string | null; label: string };
  summary: { receipts_cents: number; expenses_cents: number; result_cents: number; unallocated_cents: number; operations_count: number; rent_lines_count: number; missing_documents: number };
  lines_count: number;
  fec: { entries: number; lines: number; debit_cents: number; credit_cents: number; balanced: boolean; filename: string };
  missing: { id: string; date: string; label: string; amount_cents: number; category_label: string }[];
  warnings: string[];
  exports: { id: string; version: number; owner_id: string | null; operations_count: number; receipts_total_cents: number; expenses_total_cents: number; missing_documents: number; created_at: string }[];
  vat_applicable: boolean;
};

export function ExportPage() {
  const org = useOrg();
  const refs = useRefs();
  const invalidate = useInvalidate();
  const [year, setYear] = useState(currentYear() - 1);
  const [owner, setOwner] = useState("");
  const qs = owner ? `?owner_id=${owner}` : "";
  const q = useGet<Check>(org.api(`/finance/export/${year}/check${qs}`));
  const d = q.data;
  const canExport = org.can("finance");
  const refreshSoon = () => setTimeout(() => invalidate(org.api(`/finance/export/${year}`)), 1500);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Export comptable annuel"
        intro="Vérifiez les totaux et les pièces manquantes, puis téléchargez le registre, le fichier des écritures et l’archive des justificatifs pour votre expert-comptable."
      />
      <Notice tone="attention" title="À valider par un expert-comptable">
        Le fichier des écritures suit la structure du fichier des écritures comptables (article A47 A-1 du livre des procédures fiscales) avec des comptes proposés. Il n’est pas une déclaration fiscale. La TVA n’apparaît que pour les opérations où elle a été saisie.
      </Notice>
      <div className="finances-filtres">
        <YearSelect value={year} onChange={setYear} />
        <Select label="Propriétaire juridique" value={owner} onChange={(e) => setOwner(e.target.value)} options={[{ value: "", label: "Tout l’espace" }, ...(refs.data?.owners ?? []).map((o) => ({ value: o.id, label: o.display_name }))]} />
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {d ? (
        <>
          <div className="stats">
            <Stat label="Recettes" value={<Amount cents={d.summary.receipts_cents} />} />
            <Stat label="Dépenses" value={<Amount cents={d.summary.expenses_cents} />} />
            <Stat label="Lignes exportées" value={d.lines_count} hint={`dont ${d.summary.rent_lines_count} loyers encaissés`} />
            <Stat label="Sans justificatif" value={d.summary.missing_documents} />
          </div>
          <Card title="Contrôle avant génération">
            <KeyValues
              items={[
                ["Périmètre", d.scope.label],
                ["Période", `du ${date(d.period.from)} au ${date(d.period.to)}`],
                ["Écritures", <StatusChip status={d.fec.balanced ? "conforme" : "anomalie"} label={d.fec.balanced ? `Équilibrées : ${d.fec.entries} écritures, ${d.fec.lines} lignes` : "Écritures déséquilibrées : export à vérifier"} />],
                ["Total débit / crédit", <><Amount cents={d.fec.debit_cents} /> / <Amount cents={d.fec.credit_cents} /></>],
                ["TVA", d.vat_applicable ? "Présente sur certaines opérations" : "Aucune TVA saisie"],
                ["Nom du fichier des écritures", <code>{d.fec.filename}</code>],
              ]}
            />
            {d.warnings.length ? (
              <Notice tone="attention" title="Points à vérifier">
                <ul className="finances-formules">
                  {d.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Notice>
            ) : (
              <StatusChip status="conforme" label="Aucun point bloquant relevé" />
            )}
            {canExport ? (
              <div className="row">
                <a className="bv-btn bv-btn-primaire" href={org.api(`/finance/export/${year}/archive.zip${qs}`)} download onClick={refreshSoon}>
                  Télécharger l’archive (zip)
                </a>
                <a className="bv-btn bv-btn-secondaire" href={org.api(`/finance/export/${year}/operations.csv${qs}`)} download>
                  Registre CSV
                </a>
                <a className="bv-btn bv-btn-secondaire" href={org.api(`/finance/export/${year}/fec${qs}`)} download>
                  Fichier des écritures
                </a>
              </div>
            ) : (
              <p className="muted">Votre rôle permet de consulter ce contrôle, pas de générer l’export.</p>
            )}
          </Card>
          {d.missing.length ? (
            <Card title="Opérations sans justificatif">
              <Table caption="Opérations sans justificatif">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Libellé</th>
                    <th>Catégorie</th>
                    <th className="right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {d.missing.map((m) => (
                    <tr key={m.id}>
                      <td>{date(m.date)}</td>
                      <td>{m.label}</td>
                      <td>{m.category_label}</td>
                      <td className="right"><Amount cents={m.amount_cents} /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="small muted">Joignez les pièces depuis « Recettes et dépenses » avant de générer l’archive, ou signalez-les à votre comptable.</p>
            </Card>
          ) : null}
          <Card title="Versions générées">
            {d.exports.length === 0 ? (
              <Empty title="Aucune archive générée pour cette année." />
            ) : (
              <Table caption="Versions d’export">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Générée le</th>
                    <th className="right">Recettes</th>
                    <th className="right">Dépenses</th>
                    <th>Pièces manquantes</th>
                  </tr>
                </thead>
                <tbody>
                  {d.exports.map((e) => (
                    <tr key={e.id}>
                      <td>v{e.version}</td>
                      <td>{dateTime(e.created_at)}</td>
                      <td className="right"><Amount cents={e.receipts_total_cents} /></td>
                      <td className="right"><Amount cents={e.expenses_total_cents} /></td>
                      <td>{e.missing_documents}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <p className="small muted">Une correction après génération produit une nouvelle version ; les précédentes restent listées.</p>
          </Card>
        </>
      ) : null}
    </div>
  );
}
